import { z } from 'zod';
import { cnpj } from 'cpf-cnpj-validator';
import { TipoChavePixSchema } from './pagamento';

/**
 * Phase 24 v1.6 — REQ-PIX-01. Dados Bancários (chave PIX padrão).
 *
 * Shared schemas for the profissional default-PIX-key CRUD endpoints:
 *   GET    /profissionais/me/dados-bancarios   (200 masked DTO or 204)
 *   PUT    /profissionais/me/dados-bancarios   (body = DadosBancariosUpsertSchema)
 *   DELETE /profissionais/me/dados-bancarios   (204)
 *
 * Per CONTEXT.md D-11: discriminatedUnion on tipoChave so per-tipo format
 * validation is enforced at the schema layer. Per D-07: response shape is
 * (tipoChavePixPadrao, chaveMasked) — plaintext chave never leaves the server.
 *
 * Validation regex notes:
 *  - cpf: raw 11 digits OR masked "000.000.000-00" (frontend ngx-mask emits
 *    raw digits when stripped via valueChanges; submit path normalizes).
 *  - email: RFC-ish via z.string().email().
 *  - celular: 3 accepted forms — raw national 11-digit (DDD + 9 + 8 digits),
 *    masked "(00) 00000-0000", or E.164 "+55DDD9XXXXXXXX". Backend re-normalizes
 *    to E.164 before hashing/storing.
 *  - aleatoria: UUID v4 lowercased.
 */

const cpfBranch = z.object({
  tipoChave: z.literal('cpf'),
  chave: z
    .string()
    .regex(
      /^\d{11}$|^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
      'CPF inválido (esperado 11 dígitos ou 000.000.000-00)',
    ),
});

const cnpjBranch = z.object({
  tipoChave: z.literal('cnpj'),
  chave: z
    .string()
    .refine((s) => {
      const clean = s.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      return cnpj.isValid(clean);
    }, 'CNPJ inválido (esperado 14 caracteres alfanuméricos ou formatado)'),
});

const emailBranch = z.object({
  tipoChave: z.literal('email'),
  chave: z.string().email('E-mail inválido').max(140),
});

const celularBranch = z.object({
  tipoChave: z.literal('celular'),
  chave: z
    .string()
    .regex(
      /^(\+?55)?\s?\(?[1-9]{2}\)?\s?9\d{4}-?\d{4}$|^\+?55\d{10,11}$|^[1-9]{2}9\d{8}$/,
      'Celular inválido (esperado (DD) 9XXXX-XXXX ou +55DD9XXXXXXXX)',
    ),
});

const aleatoriaBranch = z.object({
  tipoChave: z.literal('aleatoria'),
  chave: z
    .string()
    .uuid('Chave aleatória inválida (UUID v4)'),
});

/**
 * Upsert request body for PUT /profissionais/me/dados-bancarios.
 *
 * Backend service additionally normalizes + (for tipoChave='cpf') enforces
 * the anti-mule invariant (chave must equal the actor's stored CPF). See
 * CONTEXT.md D-06.
 */
export const DadosBancariosUpsertSchema = z.discriminatedUnion('tipoChave', [
  cpfBranch,
  cnpjBranch,
  emailBranch,
  celularBranch,
  aleatoriaBranch,
]);

/**
 * Response shape for GET (when present) and PUT.
 *
 * `chaveMasked` is a server-rendered masked string (last-4 digits visible per
 * tipo) — plaintext chave is NEVER returned. Per D-07.
 */
export const DadosBancariosResponseSchema = z.object({
  tipoChavePixPadrao: TipoChavePixSchema,
  chaveMasked: z.string(),
});

export type DadosBancariosUpsertInput = z.infer<
  typeof DadosBancariosUpsertSchema
>;
export type DadosBancariosResponse = z.infer<typeof DadosBancariosResponseSchema>;
